(ns manage.adset-preview-panel.db.products
  (:require [clojure.edn :as edn]
            [clojure.string :as str]
            [common.db.interceptors :refer [forwarded-event]]
            [common.util :as util]
            [goog.crypt.base64 :as base64]
            [re-frame.core :as rf]))

(def initial-state {:product-selectors nil
                    :products-for-current-selector {}
                    :product-draft {}})

(defn product-selectors
  [schema-urns]
  (filter #(str/starts-with? (:schema %) "urn:lemonpi:schema:context:products") schema-urns))

(defn sanitize-products
  "sanitizes/prepares products for content function evaluation payload
   input shape: '({PRODUCT-SELECTOR-NAME    [{:description ' something '
                                              :title ' something '}
                                             {:description ' something-else '
                                              :title ' something-else '}]}
                  {ANOTHER-PRODUCT-SELECTOR [{:description ' another-something '}]})

   output shape: {PRODUCT-SELECTOR-NAME      {:items [{:description ' something '
                                                       :title ' something '}
                                                      {:description ' something-else '
                                                       :title ' something-else '}]}]
                                              :count 10}}
                  ANOTHER-PRODUCT-SELECTOR   {:items [{:description ' another-something '}]
                                              :count 10}

   count is the number of non-nil values but product selectors return 10 items (nils included)"
  [products-for-selectors]
  (->> (into {} products-for-selectors)
       (util/map-vals #(hash-map :items %))
       (util/map-vals (fn [val]
                        (assoc val :count (->> val
                                               :items
                                               (filter some?)
                                               count))))))

(rf/reg-event-fx :adset-preview.products/init
  [(rf/inject-cofx :query-params)]
  (fn [{:keys [db]
        {:keys [products]} :query-params}]
    (let [products-decoded (some-> products
                                   base64/decodeString
                                   edn/read-string)
          initial-state* (assoc initial-state
                                :shared-products products-decoded)]
      {:db (assoc db :adset-preview.products/products initial-state*)
       :forward-events [{:register :adset-preview.products/product-selector-result
                         :events #{:resource.product-selector/get-result-success}
                         :dispatch-to [:adset-preview.products/product-selector-result]}
                        {:register :adset-preview.products/product-get-one
                         :events #{:resource.product/get-one-for-selector-success}
                         :dispatch-to [:adset-preview.products/get-product-success]}
                        {:register :adset-preview.products/set-and-evaluate-templates-products
                         :events #{:adset-preview.products/set-product :adset-preview.products/set-product-for-selector}
                         :dispatch-to [:adset-preview.templates/evaluate-templates-context]}]})))

(rf/reg-event-fx :adset-preview.products/destroy
  (fn [_ _]
    {:forward-events [{:unregister :adset-preview.products/product-selector-result}
                      {:unregister :adset-preview.products/init-product-table}
                      {:unregister :adset-preview.products/product-get-one}
                      {:unregister :adset-preview.products/set-and-evaluate-templated-products}]}))

(rf/reg-event-fx :adset-preview.products/init-product-selectors
  [(rf/inject-cofx :sub :adset-preview.products/shared-products)]
  (fn [{:keys [db] :adset-preview.products/keys [shared-products]} [_ schema-urns]]
    (let [selectors (map #(merge % {:label (:alias %)
                                    :value (:param %)}) (product-selectors schema-urns))
          events (for [selector selectors]
                   (if (some #(= % (:param selector)) (keys shared-products))
                     [:adset-preview.products/init-shared-products (:param selector)]
                     [:resource.product-selector/get-result-by-id (:param selector)]))]
      {:db (update db :adset-preview.products/products assoc
                   :product-selectors selectors
                   :current-product-selector (first selectors))
       :dispatch-n events})))

(rf/reg-event-fx :adset-preview.products/get-product-success
  [forwarded-event
   (rf/inject-cofx :sub :adset-preview.products/shared-products)]
  (fn [{:adset-preview.products/keys [shared-products]} [_ selector-id product]]
    (let [get-indices-fn (fn [draft]
                           (keep-indexed #(when (= (:id product) %2) %1) draft))
          idx (->> (get shared-products selector-id)
                   get-indices-fn
                   first)]
      {:dispatch [:adset-preview.products/set-product-for-selector idx product selector-id]})))

(rf/reg-event-fx :adset-preview.products/product-details
  [(rf/inject-cofx :sub :adset-preview.products/shared-products)]
  (fn [{:adset-preview.products/keys [shared-products]}]
    (let [product-events (->> shared-products
                              (map #(for [product-id (second %)]
                                      (when (some? product-id)
                                        [:resource.product/get-one-for-selector
                                         {:product-id product-id
                                          :selector-param (first %)}])))
                              (map #(filter some? %))
                              (apply concat))]
      {:dispatch-n product-events})))

(rf/reg-event-db :adset-preview.products/product-selector-result
  [forwarded-event]
  (fn [db [_ selector-id {:keys [items]}]]
    (let [ten-items (->> (concat items (repeat nil))
                         (take 10)
                         vec)]
      (-> db
          (assoc-in [:adset-preview.products/products :products-for-current-selector selector-id] ten-items)))))

(rf/reg-event-fx :adset-preview.products/init-shared-products
  (fn [{:keys [db]} [_ selector-id]]
    (let [ten-items (vec (repeat 10 nil))
          new-db (-> db
                     (assoc-in [:adset-preview.products/products :products-for-current-selector selector-id] ten-items))]
      {:db new-db
       :dispatch [:adset-preview.products/product-details]})))

(rf/reg-event-db :adset-preview.products/set-product-selector
  (fn [db [_ new-selector]]
    (assoc-in db [:adset-preview.products/products :current-product-selector] new-selector)))

(rf/reg-event-fx :adset-preview.products/set-product
  [(rf/inject-cofx :sub :adset-preview.products/current-product-selector)]
  (fn [{:keys [db] :adset-preview.products/keys [current-product-selector]} [_ idx product]]
    {:db (assoc-in db [:adset-preview.products/products :products-for-current-selector (:param current-product-selector) idx] product)
     :dispatch [:modal/close]}))

(rf/reg-event-fx :adset-preview.products/set-product-for-selector
  (fn [{:keys [db]} [_ idx product selector-id]]
    {:db (update-in db [:adset-preview.products/products :products-for-current-selector selector-id] assoc idx product)}))

(rf/reg-event-fx :adset-preview.products/delete-product
  [(rf/inject-cofx :sub :adset-preview.products/current-product-selector)]
  (fn [{:keys [db] :adset-preview.products/keys [current-product-selector]} [_ idx]]
    {:db (assoc-in db [:adset-preview.products/products :products-for-current-selector (:param current-product-selector) idx] nil)}))

(rf/reg-sub :adset-preview.products/value
  (fn [db [_ path default]]
    (get-in db [:adset-preview.products/products path] default)))

(rf/reg-sub :adset-preview.products/current-product-selector
  :<- [:adset-preview.products/value :current-product-selector]
  (fn [current-product-selector]
    current-product-selector))

(rf/reg-sub :adset-preview.products/shared-products
  :<- [:adset-preview.products/value :shared-products]
  (fn [shared-products]
    shared-products))

(rf/reg-sub :adset-preview.products/products-for-current-selector
  :<- [:adset-preview.products/value :current-product-selector]
  :<- [:adset-preview.products/value :products-for-current-selector]
  (fn [[current-product-selector products-for-current-selector]]
    (let [selector-id (:param current-product-selector)]
      (get products-for-current-selector selector-id))))

(rf/reg-sub :adset-preview.products/products-for-selectors+params
  :<- [:adset-preview.products/value :product-selectors]
  :<- [:adset-preview.products/value :products-for-current-selector]
  (fn [[product-selectors products-for-current-selector] _]
    (let [products (for [selector product-selectors]
                     (hash-map (:param selector) (get products-for-current-selector (:param selector))))]
      (sanitize-products products))))

(rf/reg-sub :adset-preview.products/products-for-selectors
  :<- [:adset-preview.products/value :product-selectors]
  :<- [:adset-preview.products/value :products-for-current-selector]
  (fn [[product-selectors products-for-current-selector] _]
    (letfn [(product-fields-for-selector
              [{:keys [param]}]
              (mapv :fields (get products-for-current-selector param)))]
      (let [products (for [selector product-selectors]
                       (hash-map (keyword (:alias selector)) (product-fields-for-selector selector)))]
        (sanitize-products products)))))
